import { useEffect, useRef, useState } from 'react'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { emitSound } from '../notifications/sounds'
import { confirmedUpsert, type StoreRecord } from './chefShared'

/** Read the nested order contract without retaining a previous customer's details. */
export const useChefOrderDetail = (orderId: string) => {
  const [detail, setDetail] = useState<StoreRecord | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    let active = true
    setDetail(null)
    setError('')
    setLoading(Boolean(orderId))
    if (!orderId) return
    void commands.marketplace.getOrder(orderId).then((result) => {
      const payload = commandPayload(result)
      const order = payload.order as StoreRecord | undefined
      if (!order || String(order.id ?? '') !== orderId) throw new Error('Invalid order response')
      if (active) setDetail(payload)
    }).catch(() => {
      if (active) setError('Could not load customer details and items. Please retry before preparing this order.')
    }).finally(() => {
      if (active) setLoading(false)
    })
    return () => { active = false }
  }, [orderId, attempt])
  return { detail, setDetail, loading, error, retry: () => setAttempt((n) => n + 1) }
}

/** Publish only confirmed mutations. Failed commands leave the visible order unchanged. */
export const useOrderAdvance = (order: StoreRecord, profileId: string) => {
  const pending = useRef(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const run = async (next: string): Promise<StoreRecord | null> => {
    if (pending.current) return null
    pending.current = true
    setBusy(true)
    setError('')
    try {
      let updated = { ...order, status: next }
      if (isLiveRuntime()) {
        const result = commandPayload(await commands.marketplace.setOrderStatus(String(order.id), {
          status: next, actor_profile_id: profileId,
          reason: next === 'in_delivery' ? 'chef_confirmed_handoff'
            : next === 'completed' && String(order.delivery_choice) === 'self_pickup' ? 'chef_confirmed_collection'
              : next === 'completed' ? 'chef_reported_delivery' : '',
        }))
        if (String(result.id ?? '') !== String(order.id) || !result.status) throw new Error('Invalid order response')
        updated = { ...order, ...result, status: String(result.status) }
      }
      confirmedUpsert('marketplace.marketplace_orders', updated)
      if (['ready', 'in_delivery', 'completed'].includes(next)) {
        emitSound('order', `chef:order:${String(order.id)}:${next}`)
      }
      return updated
    } catch {
      setError('The order could not be updated. Check its current status and try again.')
      return null
    } finally {
      pending.current = false
      setBusy(false)
    }
  }
  return { busy, error, run }
}
